import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcFundingV1AccountNumberQueryServiceAccountNumberGet } from '@interxfi/api-client/generated/hooks/ixc-funding-v1-account-number-query-service-account-number-get';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcFundingV1AccountNumberState } from '@interxfi/types/generated/api';
import { FundingAccountNumberEntityMenu } from '../../../../../../features/funding/account-number/funding-account-number-entity-menu';
import { FundingAccountNumberEventSummary } from '../../../../../../features/funding/account-number/funding-account-number-event';
import { getFundingAccountNumberStateListItems } from '../../../../../../features/funding/account-number/funding-account-number-list-items';
import { getFundingAccountNumberDetailPath } from '../../../../../../features/funding/account-number/funding-account-number-routing';
import { FundingAccountNumberStatusPill } from '../../../../../../features/funding/account-number/funding-account-number-status-pill';
import { ProgramLogomark } from '../../../../../../features/userauth/program/logomark/program-logomark';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/funding/account-number/_account-number/$accountNumberId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `funding-account-number-${params.accountNumberId}`,
        label: 'Account Number',
        to: getFundingAccountNumberDetailPath(params.accountNumberId),
        suffix: (
          <CopyButton
            content={params.accountNumberId}
            label="Copy Account Number ID"
            tooltip={{ copy: params.accountNumberId, copied: 'Copied Account Number ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Account Number'),
  component: FundingAccountNumberDetail,
});

function getListItems(data: IxcFundingV1AccountNumberState | undefined) {
  const items = getFundingAccountNumberStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [
            items.data.spec.nickname,
            items.data.spec.ownerType,
            items.data.spec.sourceType.bankDetails.routingNumber,
            items.data.spec.sourceType.bankDetails.accountNumber,
            { ...items.data.spec.sourceType.bankDetails.accountHolderName, title: 'Name on Account' },
            items.data.spec.sourceType.bankDetails.accountType,
            items.data.spec.sourceType.enabled,
            ...(items.data.spec.validation ? [{ ...items.data.spec.validation.value.typeName, title: 'Validation Type' }] : []),
            ...(items.data.externalId ? [items.data.externalId] : []),
          ],
        },
      ] as const satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.accountNumberId,
        items.accountId,
        items.programId,
        items.data.sourceId,
      ] as const satisfies ReadonlyListItem[],
    },
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load account number', 'view this account number');

function FundingAccountNumberDetail() {
  const { accountNumberId } = Route.useParams();
  const request = { accountNumberId } as const;
  const { data, error, isPending } = useIxcFundingV1AccountNumberQueryServiceAccountNumberGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.accountNumber), [data?.accountNumber]);
  const { activeTenantContext } = useActiveRealmAPIContext();

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              {activeTenantContext !== TenantContext.Program ? (
                <ProgramLogomark programId={data?.accountNumber?.programId} shouldLoadLogoMark size="large" withTooltip />
              ) : null}

              <FundingAccountNumberStatusPill isPending={showPending} size="large" status={data?.accountNumber?.status} />
            </HorizontalStack>

            <FundingAccountNumberEntityMenu accountNumberId={accountNumberId} />
          </PageHeader>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FundingAccountNumberEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
